import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import styles from './Button.module.css';

type Variant = 'primary' | 'secondary' | 'ghost';
type Size = 'sm' | 'md' | 'lg';

interface CommonProps {
  variant?: Variant;
  size?: Size;
  children: ReactNode;
  fullWidth?: boolean;
  className?: string;
}

type ButtonAsButton = CommonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'className'> & {
    as?: 'button';
    to?: never;
    href?: never;
  };

type ButtonAsLink = CommonProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'children' | 'className' | 'href'> & {
    as: 'link';
    to: string;
  };

type ButtonAsAnchor = CommonProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'children' | 'className'> & {
    as: 'a';
    href: string;
  };

export type ButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsAnchor;

const classNames = (variant: Variant, size: Size, fullWidth: boolean, extra?: string): string => {
  return [
    styles.root,
    styles[`variant_${variant}`],
    styles[`size_${size}`],
    fullWidth ? styles.fullWidth : '',
    extra ?? '',
  ]
    .filter(Boolean)
    .join(' ');
};

const extractCommon = (
  props: ButtonProps,
): { variant: Variant; size: Size; fullWidth: boolean; className?: string } => {
  return {
    variant: props.variant ?? 'primary',
    size: props.size ?? 'md',
    fullWidth: props.fullWidth ?? false,
    className: props.className,
  };
};

export const Button = (props: ButtonProps) => {
  const { variant, size, fullWidth, className } = extractCommon(props);
  const finalClass = classNames(variant, size, fullWidth, className);

  if (props.as === 'link') {
    const {
      as: _as,
      variant: _v,
      size: _s,
      fullWidth: _fw,
      children,
      to,
      className: _c,
      ...rest
    } = props;
    return (
      <Link to={to} className={finalClass} {...rest}>
        {children}
      </Link>
    );
  }

  if (props.as === 'a') {
    const {
      as: _as,
      variant: _v,
      size: _s,
      fullWidth: _fw,
      children,
      href,
      className: _c,
      ...rest
    } = props;
    return (
      <a href={href} className={finalClass} {...rest}>
        {children}
      </a>
    );
  }

  const {
    as: _as,
    variant: _v,
    size: _s,
    fullWidth: _fw,
    children,
    type = 'button',
    className: _c,
    ...rest
  } = props;
  return (
    <button type={type} className={finalClass} {...rest}>
      {children}
    </button>
  );
};

Button.displayName = 'Button';
